// BookHelp.jsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

const categories = [
  {
    id: "textbook",
    title: "Textbook",
    icon: "fa-book",
    what: "Official curriculum book prescribed by school, college, or university.",
    purpose: "To teach the complete syllabus, concepts, and theory.",
    who: "Students, teachers, academic institutions.",
    india: "NCERT Books, CBSE/ICSE/State Board textbooks, IGNOU textbooks.",
    global: "Oxford Textbooks, Cambridge Textbooks, Pearson Academic Books.",
  },
  {
    id: "guidebook",
    title: "Guidebook",
    icon: "fa-lightbulb",
    what: "Simplified support book that explains textbook content easily.",
    purpose: "To help students understand topics quickly and get solved answers.",
    who: "Students preparing for exams, tutors.",
    india: "Navneet Guides, ML Aggarwal Guides, Together With series.",
    global: "CliffsNotes, SparkNotes, Barron’s Educational Guides.",
  },
  {
    id: "supplementary",
    title: "Supplementary (Xtra Learning)",
    icon: "fa-layer-group",
    what: "Additional learning resources beyond textbooks and guides.",
    purpose: "Extra practice, mock tests, worksheets, coaching materials.",
    who: "Coaching students, competitive exam aspirants, self-learners.",
    india: "Allen Modules, Aakash Modules, Arihant Practice Books, Byju’s/Unacademy content.",
    global: "Khan Academy, Kaplan Study Material, Princeton Review, Kumon Workbooks.",
  },
  {
    id: "periodical",
    title: "Periodical",
    icon: "fa-newspaper",
    what: "Regularly published educational magazines or journals.",
    purpose: "To give current updates, GK, current affairs, and monthly learning.",
    who: "Competitive exam aspirants, readers, teachers, students.",
    india: "Pratiyogita Darpan, Competition Success Review, Science Reporter.",
    global: "National Geographic, Scientific American, Time for Kids.",
  },
  {
    id: "professional",
    title: "Professional",
    icon: "fa-briefcase",
    what: "Skill-development learning materials for career growth.",
    purpose: "To improve job skills, leadership, communication, or technical expertise.",
    who: "Working professionals, trainers, students entering the job market.",
    india: "NPTEL Courses, TCS iON Guides, NIIT Training Books.",
    global: "Coursera Modules, LinkedIn Learning Guides, O’Reilly Professional Books.",
  },
  {
    id: "corporate",
    title: "Corporate",
    icon: "fa-building",
    what: "Internal company training guides, SOPs, onboarding manuals.",
    purpose: "To train employees on processes, culture, compliance, and tools.",
    who: "Employees, corporate trainers, HR/L&D teams.",
    india: "Infosys Lex Training Modules, TCS Onboarding Handbook, Wipro SOP Documents.",
    global: "Google Onboarding Guide, Amazon Leadership Training Manuals, Microsoft SOPs.",
  },
  {
    id: "collections",
    title: "Collections (Derived Category)",
    icon: "fa-folder-open",
    what: "A curated bundle of books from any one or multiple categories.",
    purpose: "To provide an organized set for complete, structured learning.",
    who: "Students, institutions, corporates, coaching centers, libraries.",
    india: "Class 10 Board Prep Combo (Textbook + Guide + Supplementary), UPSC Combo Sets.",
    global: "SAT Prep Kit (Textbook + Practice Books), Corporate L&D Learning Paths.",
  },
];

export default function BookHelp() {
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-950 via-gray-900 to-black text-gray-100 px-4 py-6 sm:px-6 lg:px-10">
      <div className="mx-auto">
        <header className="mb-2">
          <div className="flex -1 items-left gap-3 mb-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600/20 border border-indigo-400/40">
              <i className="fas fa-book-reader text-indigo-300 text-lg" />
            </span>
            <div className="flex-1 justify-start">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                Learning Material Classification
              </h1>
              <p className="text-xs sm:text-sm text-gray-400">
                Quick help to understand each category: what it is, why it exists, who uses it, and examples.
              </p>
            </div>
          </div>
          <div className="mt-3 text-xs sm:text-sm text-gray-400 bg-gray-900/60 border border-gray-800 rounded-2xl px-3 py-3">
            <p>
              Use this guide to tag or understand books, training content, and resources across academic, professional,
              and corporate learning. Optimized for on-screen reading in dark mode. 🌙
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 w-full mb-4">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="w-full lg:w-auto rounded-2xl border border-green-400 bg-gray-900/70 p-4 shadow-[0_0_30px_rgba(0,0,0,0.35)] hover:border-indigo-500/70 hover:shadow-[0_0_40px_rgba(79,70,229,0.35)] transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gray-800/80 border border-gray-700 group-hover:border-indigo-400/70">
                    <i className={`fas ${cat.icon} text-xs lg:text-sm text-yellow-200`} />
                  </span>
                  <h2 className="text-sm lg:text-base font-semibold tracking-tight text-yellow-200">
                    {cat.title}
                  </h2>
                </div>
                <span className="text-[0.65rem] sm:text-[0.7rem] uppercase tracking-wide text-white">
                  Category
                </span>
              </div>
              <div className="space-y-2 text-xs lg:text-sm leading-relaxed">
                <InfoRow label="What it is" value={cat.what} />
                <InfoRow label="Purpose" value={cat.purpose} />
                <InfoRow label="Who uses it" value={cat.who} />
                <InfoRow label="Examples in India" value={cat.india} />
                <InfoRow label="Global examples" value={cat.global} />
              </div>
            </div>
          ))}
        </div>

        <section id="boards" className="p-4 bg-black">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 text-center">Central and State Education Boards in India</h2>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-black border border-gray-200">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">State / Central</th>
                    <th className="px-4 py-2 text-left">Board / Authority</th>
                    <th className="px-4 py-2 text-left">Textbook Publisher / Body</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">

                  {/* Central Boards */}
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Central</td>
                    <td className="px-4 py-2">NCERT</td>
                    <td className="px-4 py-2">NCERT (Publishes textbooks for CBSE and many state boards)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Central</td>
                    <td className="px-4 py-2">CBSE</td>
                    <td className="px-4 py-2">NCERT (main), CBSE guidelines</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Central</td>
                    <td className="px-4 py-2">NIOS</td>
                    <td className="px-4 py-2">NIOS (Textbooks for open schooling / distance learning)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Central</td>
                    <td className="px-4 py-2">CISCE / ICSE</td>
                    <td className="px-4 py-2">CISCE approved publishers (ICSE / ISC textbooks)</td>
                  </tr>

                  {/* States */}
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Andhra Pradesh</td>
                    <td className="px-4 py-2">Andhra Pradesh Board of Secondary Education</td>
                    <td className="px-4 py-2">SCERT Andhra Pradesh</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Arunachal Pradesh</td>
                    <td className="px-4 py-2">Arunachal Pradesh Board of School Education</td>
                    <td className="px-4 py-2">SCERT Arunachal Pradesh</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Assam</td>
                    <td className="px-4 py-2">Board of Secondary Education Assam (SEBA) & AHSEC</td>
                    <td className="px-4 py-2">SCERT Assam</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Bihar</td>
                    <td className="px-4 py-2">Bihar School Examination Board (BSEB)</td>
                    <td className="px-4 py-2">Bihar SCERT / Bihar Textbook Corporation</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Chhattisgarh</td>
                    <td className="px-4 py-2">Chhattisgarh Board of Secondary Education</td>
                    <td className="px-4 py-2">Chhattisgarh SCERT / Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Delhi</td>
                    <td className="px-4 py-2">Delhi Board of Education</td>
                    <td className="px-4 py-2">Bal Bharti / Delhi SCERT</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Goa</td>
                    <td className="px-4 py-2">Goa Board of Secondary & Higher Secondary Education</td>
                    <td className="px-4 py-2">SCERT Goa</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Gujarat</td>
                    <td className="px-4 py-2">Gujarat Secondary and Higher Secondary Education Board</td>
                    <td className="px-4 py-2">Gujarat State Textbook Board</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Haryana</td>
                    <td className="px-4 py-2">Haryana Board of School Education</td>
                    <td className="px-4 py-2">Haryana SCERT / Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Himachal Pradesh</td>
                    <td className="px-4 py-2">Himachal Pradesh Board of School Education</td>
                    <td className="px-4 py-2">HP SCERT / HP Textbook Bureau</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Jharkhand</td>
                    <td className="px-4 py-2">Jharkhand Academic Council</td>
                    <td className="px-4 py-2">Jharkhand SCERT / Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Karnataka</td>
                    <td className="px-4 py-2">Karnataka Secondary Education Examination Board</td>
                    <td className="px-4 py-2">SCERT Karnataka / Karnataka Textbook Society</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Kerala</td>
                    <td className="px-4 py-2">Kerala State Education Board</td>
                    <td className="px-4 py-2">SCERT Kerala / State Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Madhya Pradesh</td>
                    <td className="px-4 py-2">Madhya Pradesh Board of Secondary Education</td>
                    <td className="px-4 py-2">SCERT MP / MP Textbook Bureau</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Maharashtra</td>
                    <td className="px-4 py-2">Maharashtra State Board of Secondary & Higher Secondary Education</td>
                    <td className="px-4 py-2">Maharashtra SCERT / State Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Manipur</td>
                    <td className="px-4 py-2">Board of Secondary Education Manipur</td>
                    <td className="px-4 py-2">SCERT Manipur</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Meghalaya</td>
                    <td className="px-4 py-2">Meghalaya Board of School Education</td>
                    <td className="px-4 py-2">SCERT Meghalaya</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Mizoram</td>
                    <td className="px-4 py-2">Mizoram Board of School Education</td>
                    <td className="px-4 py-2">SCERT Mizoram</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Nagaland</td>
                    <td className="px-4 py-2">Nagaland Board of School Education</td>
                    <td className="px-4 py-2">SCERT Nagaland</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Odisha</td>
                    <td className="px-4 py-2">Board of Secondary Education Odisha</td>
                    <td className="px-4 py-2">SCERT Odisha / Odisha Textbook Bureau</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Punjab</td>
                    <td className="px-4 py-2">Punjab School Education Board (PSEB)</td>
                    <td className="px-4 py-2">Punjab SCERT / Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Rajasthan</td>
                    <td className="px-4 py-2">Board of Secondary Education Rajasthan</td>
                    <td className="px-4 py-2">Rajasthan SCERT / State Textbook Bureau</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Sikkim</td>
                    <td className="px-4 py-2">Sikkim Board of School Education</td>
                    <td className="px-4 py-2">SCERT Sikkim</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Tamil Nadu</td>
                    <td className="px-4 py-2">Tamil Nadu State Board</td>
                    <td className="px-4 py-2">Tamil Nadu Textbook Corporation</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Telangana</td>
                    <td className="px-4 py-2">Telangana Board of Secondary Education</td>
                    <td className="px-4 py-2">SCERT Telangana / Telangana Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Tripura</td>
                    <td className="px-4 py-2">Tripura Board of Secondary Education</td>
                    <td className="px-4 py-2">SCERT Tripura</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">Uttar Pradesh</td>
                    <td className="px-4 py-2">Uttar Pradesh Madhyamik Shiksha Parishad (UPMSP)</td>
                    <td className="px-4 py-2">UP SCERT / State Textbook Bureau</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Uttarakhand</td>
                    <td className="px-4 py-2">Board of School Education Uttarakhand</td>
                    <td className="px-4 py-2">SCERT Uttarakhand / Textbook Bureau</td>
                  </tr>
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-4 py-2">West Bengal</td>
                    <td className="px-4 py-2">West Bengal Board of Secondary Education</td>
                    <td className="px-4 py-2">SCERT WB / Textbook Bureau</td>
                  </tr>

                </tbody>
              </table>
            </div>

            <p className="mt-4 text-sm text-white">
              Note: Each state has a SCERT (State Council of Educational Research & Training) responsible for preparing, publishing, and approving textbooks. Other notable bodies include BTBC and RIEs.
            </p>
          </div>
        </section>


        <footer className="bg-gray-900 border-t border-gray-800 text-gray-400 py-4">
          <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about-us" className="hover:text-cyan-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-cyan-300">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-cyan-300">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-cyan-300">Contact</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="border-l border-gray-700 pl-3">
      <div className="text-[0.65rem] sm:text-[0.7rem] uppercase tracking-wide text-gray-500 flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-indigo-400/80" />
        {label}
      </div>
      <p className="mt-0.5 text-white text-xs sm:text-sm">
        {value}
      </p>
    </div>
  );
}

